import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Game, Player } from '../types/game.types';

type SetupStep = 'game-details' | 'add-players' | 'generate-world' | 'ready';

interface GameState {
  // Current game
  currentGame: Game | null;
  players: Player[];
  recentGames: Game[]; // List of recently created/joined games

  // UI state
  isLoading: boolean;
  error: string | null;
  setupStep: SetupStep;

  // A paused, not-yet-generated game the user backed out of mid-setup, so they can resume it
  // later from the home screen instead of losing their work.
  draft: { game: Game; players: Player[]; step: SetupStep } | null;

  // Actions
  setCurrentGame: (game: Game | null) => void;
  setPlayers: (players: Player[]) => void;
  addPlayer: (player: Player) => void;
  removePlayer: (playerId: string) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  setSetupStep: (step: SetupStep) => void;
  addRecentGame: (game: Game) => void;
  saveDraftAndExit: () => void;
  resumeDraft: () => void;
  discardDraft: () => void;
  reset: () => void;
}

export const useGameStore = create<GameState>()(
  persist(
    (set) => ({
      // Initial state
      currentGame: null,
      players: [],
      recentGames: [],
      isLoading: false,
      error: null,
      setupStep: 'game-details',
      draft: null,

      // Actions
      setCurrentGame: (game) => set({ currentGame: game }),

      setPlayers: (players) => set({ players }),

      addPlayer: (player) =>
        set((state) => ({ players: [...state.players, player] })),

      removePlayer: (playerId) =>
        set((state) => ({
          players: state.players.filter((p) => p.id !== playerId),
        })),

      setLoading: (isLoading) => set({ isLoading }),

      setError: (error) => set({ error }),

      setSetupStep: (step) => set({ setupStep: step }),

      addRecentGame: (game) =>
        set((state) => {
          // Keep only last 10 games
          const filtered = state.recentGames.filter(g => g.id !== game.id);
          return {
            recentGames: [game, ...filtered].slice(0, 10),
          };
        }),

      // Back out of an in-progress (not yet generated) setup: stash it as a draft and return to
      // the home screen. Does nothing destructive if there's no active game.
      saveDraftAndExit: () =>
        set((state) => ({
          draft: state.currentGame
            ? { game: state.currentGame, players: state.players, step: state.setupStep }
            : state.draft,
          currentGame: null,
          players: [],
          isLoading: false,
          error: null,
          setupStep: 'game-details',
        })),

      resumeDraft: () =>
        set((state) =>
          state.draft
            ? {
                currentGame: state.draft.game,
                players: state.draft.players,
                setupStep: state.draft.step,
                draft: null,
                isLoading: false,
                error: null,
              }
            : {}),

      discardDraft: () => set({ draft: null }),

      reset: () =>
        set({
          currentGame: null,
          players: [],
          isLoading: false,
          error: null,
          setupStep: 'game-details',
          // Keep recentGames and any saved draft on reset
        }),
    }),
    {
      name: 'rpg-multiverse-game-store', // localStorage key
      partialize: (state) => ({
        currentGame: state.currentGame,
        players: state.players,
        setupStep: state.setupStep,
        recentGames: state.recentGames,
        draft: state.draft,
        // Don't persist: isLoading, error
      }),
    }
  )
);
